import { Icon } from "@iconify/react";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
} from "@/components/ui/Table";
import {
  useGenerateTenderRfq,
  useGenerateTenderRfqJob,
  useTenderRfq,
} from "@/hooks/useRfq";
import { useRfqJobsStore } from "@/stores/rfqJobs";
import { formatTHB } from "@/lib/utils";

interface RfqTabProps {
  tenderId: string;
}

interface GenerateErrorShape {
  status?: number;
  message?: string;
  originalError?: unknown;
  response?: {
    error?: {
      code?: string;
      message?: string;
      details?: unknown;
    };
    code?: string;
    message?: string;
    details?: unknown;
  };
}

function formatBahtSuffix(satang: number | null | undefined): string {
  if (satang == null) return "-";
  const thb = satang / 100;
  const value = new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(thb);
  return `${value} บาท`;
}

function formatDebugValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") {
    return value.length > 4000 ? `${value.slice(0, 4000)}\n...truncated` : value;
  }
  if (value instanceof Error) {
    return value.message;
  }
  try {
    const serialized = JSON.stringify(value, null, 2);
    if (!serialized) return null;
    return serialized.length > 4000
      ? `${serialized.slice(0, 4000)}\n...truncated`
      : serialized;
  } catch {
    return String(value);
  }
}

function GenerateErrorPanel({
  message,
  code,
  status,
  details,
}: {
  message: string | null;
  code: string | null;
  status: number | null;
  details: string | null;
}) {
  if (!message && !details) return null;

  return (
    <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-3 text-left">
      <p className="text-sm font-medium text-rose-700">
        ไม่สามารถสร้างบทสรุปผู้บริหารได้
      </p>
      {message && <p className="text-sm text-rose-700 mt-1">{message}</p>}
      {(code || status) && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-rose-700/90">
          {code && <span>code: {code}</span>}
          {status !== null && <span>status: {status}</span>}
        </div>
      )}
      {details && (
        <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded border border-rose-200 bg-white/80 p-2 text-xs text-rose-900">
          {details}
        </pre>
      )}
    </div>
  );
}

export function TenderRfqTab({ tenderId }: RfqTabProps) {
  const generateRfq = useGenerateTenderRfq();
  const persistedJob = useRfqJobsStore((state) => state.jobsByTender[tenderId]);
  const setQueuedJob = useRfqJobsStore((state) => state.setQueuedJob);
  const activeJobId = generateRfq.data?.jobId || persistedJob?.jobId;
  const generationJob = useGenerateTenderRfqJob(activeJobId);
  const currentJob = generationJob.data || persistedJob;

  const pollUntilUpdatedAfter =
    currentJob && currentJob.status !== "failed"
      ? currentJob.createdAt
      : undefined;
  const { data: rfqData, isLoading } = useTenderRfq(tenderId, {
    pollUntilUpdatedAfter,
  });

  const generationStatus = currentJob?.status;
  const isGenerating =
    generateRfq.isPending ||
    generationStatus === "queued" ||
    generationStatus === "running";

  const triggerGenerate = (force = false) => {
    generateRfq.reset();
    generateRfq.mutate(
      { tenderId, force },
      {
        onSuccess: (job) => {
          setQueuedJob({
            jobId: job.jobId,
            tenderId: job.tenderId || tenderId,
            model: job.model,
            createdAt: job.createdAt,
            status:
              job.status === "queued" || job.status === "running" || job.status === "succeeded" || job.status === "failed"
                ? job.status
                : "queued",
          });
        },
      }
    );
  };

  const generationJobError = generationStatus === "failed"
    ? (generationJob.data?.error || (persistedJob?.errorCode || persistedJob?.errorMessage
      ? {
          code: persistedJob?.errorCode,
          message: persistedJob?.errorMessage,
        }
      : null))
    : null;
  const generationError = generateRfq.error as GenerateErrorShape | null;
  const generationErrorCode =
    generationJobError?.code ||
    generationError?.response?.error?.code ||
    generationError?.response?.code ||
    null;
  const generationErrorMessage =
    generationJobError?.message ||
    generationError?.response?.error?.message ||
    generationError?.response?.message ||
    generationError?.message ||
    null;
  const generationErrorStatus = generationError?.status ?? null;
  const generationErrorDetails = formatDebugValue(
    generationJobError?.details ??
    generationError?.response?.error?.details ??
      generationError?.response?.details ??
      generationError?.originalError,
  );

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
          <div className="mt-4 space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </div>
      </Card>
    );
  }

  if (!rfqData) {
    return (
      <Card className="p-8 text-center">
        <Icon
          icon="solar:checklist-line-duotone"
          className="w-12 h-12 text-slate-300 mx-auto mb-3"
        />
        <h3 className="font-semibold text-slate-900">ไม่มีบทสรุปผู้บริหาร</h3>
        <p className="text-sm text-slate-500 mt-1">
          รายละเอียดบทสรุปผู้บริหารจะแสดงที่นี่เมื่อพร้อมใช้งาน
        </p>
        <div className="mt-4 flex justify-center">
          <Button
            icon="solar:document-text-line-duotone"
            loading={isGenerating}
            onClick={() => triggerGenerate()}
          >
            เริ่ม OCR และสร้างบทสรุปผู้บริหาร
          </Button>
        </div>
        <GenerateErrorPanel
          message={generationErrorMessage}
          code={generationErrorCode}
          status={generationErrorStatus}
          details={generationErrorDetails}
        />
        {isGenerating && !generationError && (
          <p className="text-sm text-emerald-600 mt-3">
            ระบบกำลังประมวลผลเอกสารและสร้างบทสรุปผู้บริหาร...
          </p>
        )}
        {generationStatus === "succeeded" && !generationError && (
          <p className="text-sm text-emerald-600 mt-2">
            สร้างบทสรุปผู้บริหารเสร็จแล้ว กำลังโหลดข้อมูล...
          </p>
        )}
      </Card>
    );
  }

  const { items } = rfqData;

  // Calculate totals
  const totalAmount = items.reduce(
    (sum, item) => sum + (item.line_total || 0),
    0,
  );
  const totalItems = items.length;

  return (
    <div className="space-y-6">
      {/* RFQ Summary */}
      <Card>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display font-semibold text-slate-900">
              สร้างบทสรุปผู้บริหาร
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {totalItems} รายการ • รวม: {formatBahtSuffix(totalAmount)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              icon="solar:refresh-line-duotone"
              loading={isGenerating}
              onClick={() => triggerGenerate(true)}
            >
              สร้างใหม่
            </Button>
            <Badge variant="secondary">
              <Icon icon="solar:checklist-line-duotone" className="w-3.5 h-3.5" />
              บทสรุปผู้บริหาร
            </Badge>
          </div>
        </div>
        <GenerateErrorPanel
          message={generationErrorMessage}
          code={generationErrorCode}
          status={generationErrorStatus}
          details={generationErrorDetails}
        />

        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
          <div>
            <p className="text-xs text-slate-500">จำนวนรายการ</p>
            <p className="font-display text-lg font-semibold text-slate-900">
              {totalItems}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">ยอดรวมประมาณการ</p>
            <p className="font-display text-lg font-semibold text-slate-900">
              {formatBahtSuffix(totalAmount)}
            </p>
          </div>
        </div>
      </Card>

      {/* Line items table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">#</TableHead>
            <TableHead>รายละเอียด</TableHead>
            <TableHead className="w-24 text-right">จำนวน</TableHead>
            <TableHead className="w-24">หน่วย</TableHead>
            <TableHead className="w-32 text-right">ราคาต่อหน่วย</TableHead>
            <TableHead className="w-32 text-right">รวม</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableEmpty colSpan={6} message="ไม่พบรายการ" />
          ) : (
            items.map((item) => (
              <TableRow key={item.id}>
                <TableCell className="text-slate-500">
                  {item.line_number}
                </TableCell>
                <TableCell>
                  <p className="font-medium text-slate-900">
                    {item.description}
                  </p>
                  {item.specifications && (
                    <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                      {item.specifications}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {item.quantity?.toLocaleString() || "-"}
                </TableCell>
                <TableCell className="text-slate-600">
                  {item.unit || "-"}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {item.unit_price ? formatTHB(item.unit_price) : "-"}
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {item.line_total ? formatTHB(item.line_total) : "-"}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Total row */}
      {items.length > 0 && (
        <div className="flex justify-end">
          <Card className="p-4 w-64">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">รวมทั้งสิ้น</span>
              <span className="font-display text-xl font-bold text-slate-900">
                {formatTHB(totalAmount)}
              </span>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
